
import { useCallback, useState } from 'react'

type HistoryState<T> = {
  past: T[]
  present: T
  future: T[]
}

const MAX_HISTORY = 100

type Updater<T> = T | ((current: T) => T)

function resolve<T>(updater: Updater<T>, current: T): T {
  return typeof updater === 'function' ? (updater as (current: T) => T)(current) : updater
}

function pushPast<T>(past: T[], entry: T): T[] {
  if (past.length >= MAX_HISTORY) {
    return [...past.slice(past.length - MAX_HISTORY + 1), entry]
  }
  return [...past, entry]
}

export type History<T> = {
  present: T
  set: (updater: Updater<T>) => void
  replace: (updater: Updater<T>) => void
  commit: (snapshot: T) => void
  reset: (value: T) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
}

export function useHistory<T>(initial: T): History<T> {
  const [state, setState] = useState<HistoryState<T>>({ past: [], present: initial, future: [] })

  const set = useCallback((updater: Updater<T>) => {
    setState((current) => {
      const next = resolve(updater, current.present)
      if (Object.is(next, current.present)) return current
      return { past: pushPast(current.past, current.present), present: next, future: [] }
    })
  }, [])

  const replace = useCallback((updater: Updater<T>) => {
    setState((current) => {
      const next = resolve(updater, current.present)
      if (Object.is(next, current.present)) return current
      return { ...current, present: next }
    })
  }, [])

  const commit = useCallback((snapshot: T) => {
    setState((current) => {
      if (Object.is(snapshot, current.present)) return current
      return { past: pushPast(current.past, snapshot), present: current.present, future: [] }
    })
  }, [])

  const reset = useCallback((value: T) => {
    setState({ past: [], present: value, future: [] })
  }, [])

  const undo = useCallback(() => {
    setState((current) => {
      if (current.past.length === 0) return current
      const previous = current.past[current.past.length - 1]
      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future],
      }
    })
  }, [])

  const redo = useCallback(() => {
    setState((current) => {
      if (current.future.length === 0) return current
      const [next, ...rest] = current.future
      return {
        past: [...current.past, current.present],
        present: next,
        future: rest,
      }
    })
  }, [])

  return {
    present: state.present,
    set,
    replace,
    commit,
    reset,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  }
}
